// Import React hooks for state management and lifecycle effects
import { useState, useEffect } from 'react';
// Import the safe authentication hook to get current user
import { useSafeAuth } from './useSafeAuth';
// Import database functions for user management
import { db } from '../lib/db';

// Cache for database user ID mapping - avoids repeated database lookups
// Maps Stack Auth ID to database user ID with timestamp for expiration
const dbUserCache = new Map<string, { dbUserId: string; timestamp: number }>();
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Request deduplication: Track active database requests to prevent race conditions
// Ensures multiple components don't trigger duplicate user creation attempts
const inFlightRequests = new Map<string, Promise<{ id: string }>>();

/**
 * Custom hook for getting database user ID from Stack Auth user
 * 
 * This hook bridges Stack Auth's user system with our internal database:
 * - Converts Stack Auth user ID to our database user ID
 * - Creates database user record if it doesn't exist
 * - Caches results to avoid repeated database calls
 * - Handles request deduplication to prevent race conditions
 * - Provides loading state during user lookup/creation
 * 
 * Performance optimizations:
 * - 30-minute cache to reduce database queries
 * - Request deduplication prevents multiple simultaneous user creation attempts
 * - Safe error handling with fallback states
 * 
 * Returns:
 * - dbUserId: string | null - The database user ID (null if not authenticated)
 * - loading: boolean - True while looking up or creating user
 */
export function useDbUser() {
  // Get current authenticated user (local auth)
  const { user } = useSafeAuth();
  // State to hold the database user ID (null when not authenticated)
  const [dbUserId, setDbUserId] = useState<string | null>(null);
  // State to track if we're currently looking up/creating the database user
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !user.id) {
      setDbUserId(null);
      setLoading(false);
      return;
    }

    // Capture user data to prevent race conditions during async operations
    const userId = user.id;
    const userEmail = user.email;
    
    // Extra safety check
    if (!userId) {
      console.error('useDbUser: userId is falsy after capture', { user, userId });
      setDbUserId(null);
      setLoading(false);
      return;
    }

    const initializeDbUser = async () => {
      // Check cache first
      const cached = dbUserCache.get(userId);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setDbUserId(cached.dbUserId);
        setLoading(false);
        return;
      }

      // In local auth, the JWT subject is our DB user id. No roundtrip needed.
      dbUserCache.set(userId, { dbUserId: userId, timestamp: Date.now() });
      setDbUserId(userId);
      setLoading(false);
    };

    initializeDbUser();
  }, [user]);

  // Return the database user ID and loading state for components to use
  return { dbUserId, loading };
}

/**
 * Clears the database user ID cache
 * 
 * This function should be called when a user logs out to ensure
 * that cached user data doesn't persist across different user sessions.
 * The cache is automatically cleared to prevent data leakage between users.
 */
export function clearDbUserCache() {
  // Remove all cached user ID mappings
  dbUserCache.clear();
}
